import { Budget } from '../types/budget';
import { VersionedBudget, BudgetVersionHistory, VersioningState, VersionNavigationInfo } from '../types/versioning';

const VERSIONING_STORAGE_KEY = 'budget_app_versioning';
export const MAX_VERSIONS_PER_BUDGET = 50; // Limit to prevent localStorage bloat
export const VERSION_CREATED_EVENT = 'VERSION_CREATED_EVENT';

// Get versioning state from localStorage
const getVersioningState = (): VersioningState => {
  try {
    const data = localStorage.getItem(VERSIONING_STORAGE_KEY);
    if (!data) {
      return { histories: {} };
    }
    
    const parsed = JSON.parse(data);
    
    // Convert timestamp strings back to Date objects
    const processedHistories: Record<number, BudgetVersionHistory> = {};
    Object.entries(parsed.histories || {}).forEach(([budgetId, history]: [string, unknown]) => {
      const h = history as BudgetVersionHistory;
      processedHistories[parseInt(budgetId)] = {
        ...h,
        versions: (h.versions || []).map((version) => ({
          ...version,
          timestamp: new Date(version.timestamp),
        })),
      };
    });
    
    return { histories: processedHistories };
  } catch (error) {
    console.error('Error parsing versioning state:', error);
    return { histories: {} };
  }
};

// Save versioning state to localStorage
const setVersioningState = (state: VersioningState): void => {
  try {
    localStorage.setItem(VERSIONING_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving versioning state:', error);
  }
};

// Create a new version for a budget
export const createVersion = (budget: Budget, description?: string): void => {
  if (!budget.id) {
    console.warn('Cannot create version for budget without ID');
    return;
  }

  const state = getVersioningState();
  const budgetId = budget.id;
  
  // Get or create history for this budget
  let history = state.histories[budgetId];
  if (!history) {
    history = {
      budgetId,
      versions: [],
      currentVersionIndex: -1,
    };
  }

  // If we're not at the latest version, remove all versions after current index
  // This implements the "branch from current version" strategy
  if (history.currentVersionIndex < history.versions.length - 1) {
    history.versions = history.versions.slice(0, history.currentVersionIndex + 1);
  }

  // Create new version
  const newVersion: VersionedBudget = {
    id: Date.now(), // Simple ID based on timestamp
    budget: JSON.parse(JSON.stringify(budget)), // Deep clone
    timestamp: new Date(),
    description,
  };

  // Add new version
  history.versions.push(newVersion);
  history.currentVersionIndex = history.versions.length - 1;

  // Limit number of versions to prevent localStorage bloat
  if (history.versions.length > MAX_VERSIONS_PER_BUDGET) {
    const removeCount = history.versions.length - MAX_VERSIONS_PER_BUDGET;
    history.versions.splice(0, removeCount);
    history.currentVersionIndex -= removeCount;
  }

  // Update state
  state.histories[budgetId] = history;
  setVersioningState(state);

  // Dispatch event for Convex sync
  window.dispatchEvent(
    new CustomEvent(VERSION_CREATED_EVENT, {
      detail: {
        budgetId,
        version: newVersion,
        timestamp: new Date(),
      },
    })
  );
};

// Navigate to previous version
export const goToPreviousVersion = (budgetId: number): Budget | null => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.currentVersionIndex <= 0) {
    return null;
  }

  history.currentVersionIndex--;
  state.histories[budgetId] = history;
  setVersioningState(state);

  return history.versions[history.currentVersionIndex].budget;
};

// Navigate to next version
export const goToNextVersion = (budgetId: number): Budget | null => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.currentVersionIndex >= history.versions.length - 1) {
    return null;
  }

  history.currentVersionIndex++;
  state.histories[budgetId] = history;
  setVersioningState(state);

  return history.versions[history.currentVersionIndex].budget;
};

// Get current version info for navigation UI
export const getVersionNavigationInfo = (budgetId: number): VersionNavigationInfo => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.versions.length === 0) {
    return {
      canGoBack: false,
      canGoForward: false,
      currentVersionIndex: 0,
      totalVersions: 0,
    };
  }

  const currentVersion = history.versions[history.currentVersionIndex];
  
  return {
    canGoBack: history.currentVersionIndex > 0,
    canGoForward: history.currentVersionIndex < history.versions.length - 1,
    currentVersionIndex: history.currentVersionIndex,
    totalVersions: history.versions.length,
    currentVersionTimestamp: currentVersion?.timestamp,
  };
};

// Get current version of a budget
export const getCurrentVersion = (budgetId: number): Budget | null => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.versions.length === 0) {
    return null;
  }

  return history.versions[history.currentVersionIndex].budget;
};

// Initialize versioning for a new budget (create first version)
export const initializeVersioning = (budget: Budget): void => {
  if (!budget.id) return;
  
  const state = getVersioningState();
  
  // Only initialize if no history exists
  if (!state.histories[budget.id]) {
    createVersion(budget, 'Initial version');
  }
};

// Clean up versioning data for deleted budgets
export const cleanupVersioningForBudget = (budgetId: number): void => {
  const state = getVersioningState();
  delete state.histories[budgetId];
  setVersioningState(state);
};

// Check if we're currently viewing a historical version (not the latest)
export const isViewingHistoricalVersion = (budgetId: number): boolean => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.versions.length === 0) {
    return false;
  }

  return history.currentVersionIndex < history.versions.length - 1;
};

// Undo last change (go to previous version)
export const undoLastChange = (budgetId: number): Budget | null => {
  return goToPreviousVersion(budgetId);
};

// Redo last undone change (go to next version)
export const redoLastChange = (budgetId: number): Budget | null => {
  return goToNextVersion(budgetId);
};

// Check if undo is available
export const canUndo = (budgetId: number): boolean => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.versions.length === 0) {
    return false;
  }

  return history.currentVersionIndex > 0;
};

// Check if redo is available
export const canRedo = (budgetId: number): boolean => {
  const state = getVersioningState();
  const history = state.histories[budgetId];
  
  if (!history || history.versions.length === 0) {
    return false;
  }

  return history.currentVersionIndex < history.versions.length - 1;
};